import notificationStore from "./notificationStore";
import clientLog from "./clientLog";

// Masaüstü (işletim sistemi) bildirimleri: izin akışı ve gösterim.
//
// İzin artık sayfa açılışında istenmiyor. Kullanıcı etkileşimi olmadan yapılan
// requestPermission çağrısını Chrome "sessiz" ikona çeviriyor, Safari ise
// tamamen yok sayıyordu; kullanıcıların çoğu "default" durumda kalıyordu.
// Şimdi: ilk tıklamada (24 saatte bir) ya da bildirim panelindeki düğmeyle.
const ASKED_AT_KEY = "notificationPermissionAskedAt";
const ASK_COOLDOWN_MS = 24 * 3600 * 1000;
const SW_LOOKUP_TIMEOUT_MS = 2000;
const ICON = "/android-chrome-192x192.png";

let armed = false;
let watching = false;

function supported() {
  return typeof window !== "undefined" && "Notification" in window;
}

function permission() {
  return supported() ? Notification.permission : "unsupported";
}

function readAskedAt() {
  try {
    return parseInt(localStorage.getItem(ASKED_AT_KEY), 10) || 0;
  } catch (e) {
    return 0;
  }
}

function writeAskedAt() {
  try {
    localStorage.setItem(ASKED_AT_KEY, String(Date.now()));
  } catch (e) {
    /* yoksay */
  }
}

// Store'daki izin durumunu tarayıcıyla eşitler; değiştiyse loglar.
function syncPermission() {
  const current = permission();
  const previous = notificationStore.state.permission;
  if (previous !== current) {
    notificationStore.setPermission(current);
    if (previous) {
      clientLog.info("notify.permission_changed", {
        from: previous,
        to: current
      });
    }
  }
  return current;
}

// Kullanıcı etkileşimi içinden çağrılmalı (click handler).
function request(trigger) {
  if (!supported()) return Promise.resolve("unsupported");
  if (Notification.permission !== "default") {
    return Promise.resolve(syncPermission());
  }
  writeAskedAt();
  return new Promise(resolve => {
    let done = false;
    const finish = result => {
      if (done) return;
      done = true;
      resolve(result || Notification.permission);
    };
    try {
      // Eski Safari sadece callback imzasını destekler, Promise dönmez.
      const pending = Notification.requestPermission(finish);
      if (pending && pending.then) {
        pending.then(finish, () => finish(Notification.permission));
      }
    } catch (e) {
      finish(Notification.permission);
    }
  }).then(result => {
    clientLog.info("notify.permission_requested", { trigger, result });
    syncPermission();
    return result;
  });
}

function armFirstClickPrompt() {
  if (armed || permission() !== "default") return;
  if (Date.now() - readAskedAt() < ASK_COOLDOWN_MS) return;
  armed = true;
  const onClick = () => {
    document.removeEventListener("click", onClick, true);
    // Başka bir sekme bu arada sormuş olabilir; süre tıklama anında kontrol
    // edilir ki aynı gün iki kez izin penceresi çıkmasın.
    if (permission() !== "default") return;
    if (Date.now() - readAskedAt() < ASK_COOLDOWN_MS) return;
    request("first-click");
  };
  document.addEventListener("click", onClick, true);
}

// Kullanıcı izni tarayıcı ayarlarından değiştirirse rozet/panel güncellensin.
function watch() {
  syncPermission();
  if (watching) return;
  watching = true;
  window.addEventListener("focus", syncPermission);
  try {
    navigator.permissions
      .query({ name: "notifications" })
      .then(status => {
        status.onchange = syncPermission;
      })
      .catch(() => {});
  } catch (e) {
    /* Safari: permissions API yok, focus yeterli */
  }
}

function withTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("sw-timeout")), ms);
    promise.then(
      value => {
        clearTimeout(timer);
        resolve(value);
      },
      err => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

function showViaServiceWorker(title, options) {
  if (!navigator.serviceWorker) {
    return Promise.reject(new Error("sw-unsupported"));
  }
  return withTimeout(
    navigator.serviceWorker.getRegistration(),
    SW_LOOKUP_TIMEOUT_MS
  ).then(registration => {
    if (!registration) throw new Error("sw-not-registered");
    return registration.showNotification(title, options);
  });
}

/**
 * Masaüstü bildirimi gösterir. Sonucu metin olarak döner (loglanır):
 *   "shown:constructor" | "shown:sw" | "skipped:<izin>" | "failed"
 *
 * Önce `new Notification` denenir: tıklanınca sekmeyi öne getirip lead'e
 * gidebilir. Android Chrome bu kurucuyu desteklemez ("Illegal constructor");
 * o durumda service worker üzerinden gösterilir.
 */
function show(item, onClick) {
  const current = syncPermission();
  if (current !== "granted") return Promise.resolve("skipped:" + current);

  const title = item.title || "Bildirim";
  const options = {
    body: (item.lead_name ? item.lead_name + " — " : "") + (item.body || ""),
    icon: ICON,
    // Aynı bildirim birden fazla sekmede tek kez görünsün.
    tag: "crm-notification-" + (item.key || item.id || title)
  };

  try {
    const notification = new Notification(title, options);
    notification.onclick = () => {
      try {
        window.focus();
      } catch (e) {
        /* yoksay */
      }
      notification.close();
      if (onClick) onClick();
    };
    notification.onerror = () => {
      clientLog.warn("notify.desktop_error", { via: "constructor" });
    };
    return Promise.resolve("shown:constructor");
  } catch (constructorError) {
    return showViaServiceWorker(title, options).then(
      () => "shown:sw",
      swError => {
        clientLog.warn("notify.desktop_failed", {
          message: String(swError && swError.message),
          constructor: String(constructorError && constructorError.message)
        });
        return "failed";
      }
    );
  }
}

function test() {
  return show({
    key: "test",
    title: "Test bildirimi",
    body: "Masaüstü bildirimleri çalışıyor."
  }).then(outcome => {
    clientLog.info("notify.test", { outcome });
    return outcome;
  });
}

// Şikâyet eden kullanıcının neden bildirim alamadığını tek satırda gösterir.
function health() {
  let standalone = false;
  try {
    standalone = window.matchMedia("(display-mode: standalone)").matches;
  } catch (e) {
    /* yoksay */
  }
  return {
    permission: permission(),
    secure: !!window.isSecureContext,
    sw: !!navigator.serviceWorker,
    swControlled: !!(
      navigator.serviceWorker && navigator.serviceWorker.controller
    ),
    visibility: document.visibilityState,
    online: navigator.onLine,
    standalone,
    askedAt: readAskedAt() || null
  };
}

export default {
  supported,
  permission,
  syncPermission,
  request,
  armFirstClickPrompt,
  watch,
  show,
  test,
  health
};
